import { Button } from '@heidi/design-system/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';

import { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import { offlineGatingMessages } from '@/app-shell/offline-recording/offline-gating-messages';
import { UpgradeCtaTierIcon } from '@heidi/commerce';
import { type CTALocation, useUpgradeCta } from './use-upgrade-cta';

export type { CTALocation };

interface UpgradeCTAButtonProps {
  // This location is for the purpose of styling. i.e. the sidebar cta button should look slightly different to the header.
  location?: CTALocation;
  className?: string;
  iconOnly?: boolean;
}

const UpgradeCTAButton = memo(
  ({ location = 'header', className, iconOnly }: UpgradeCTAButtonProps) => {
    const {
      shouldRender,
      buttonText,
      isPracticeVariant,
      isInReverseTrial,
      trialTooltipText,
      isOfflineGated,
      hidesHeaderUpgradeCta,
      handleClick,
    } = useUpgradeCta(location);

    if (!shouldRender || hidesHeaderUpgradeCta) {
      return null;
    }

    const button = (
      <Button
        iconOnly={iconOnly}
        disableAutomaticSvgSizing
        size={'xs'}
        variant={isPracticeVariant ? 'pro_plus' : 'pro'}
        className={className}
        onClick={handleClick}
        disabled={isOfflineGated}
        aria-label={buttonText}
      >
        <UpgradeCtaTierIcon
          isPracticeVariant={isPracticeVariant}
          className="size-3 justify-self-center"
        />
        {!iconOnly && <TypographyCaption>{buttonText}</TypographyCaption>}
      </Button>
    );

    return (
      <Tooltip>
        {/*
          A disabled `<button>` emits no pointer events, and the DS recipe adds
          `disabled:pointer-events-none` on top, so it cannot drive the tooltip
          that explains why it is disabled. Offline, the trigger becomes a
          hoverable wrapper with the button inert inside it — the same shape
          `heidi-upgrade-base-modal` already uses for its disabled primary CTA.
          Online the button stays the trigger, so presentation is unchanged.
        */}
        <TooltipTrigger asChild>
          {isOfflineGated ? <span className="inline-flex">{button}</span> : button}
        </TooltipTrigger>

        {(isOfflineGated || isInReverseTrial) && (
          <TooltipContent side="bottom">
            <TypographyCaption>
              {isOfflineGated ? (
                <FormattedMessage {...offlineGatingMessages.notAvailableOffline} />
              ) : (
                trialTooltipText
              )}
            </TypographyCaption>
          </TooltipContent>
        )}
      </Tooltip>
    );
  }
);

UpgradeCTAButton.displayName = 'UpgradeCTAButton';

export default UpgradeCTAButton;
